<template>
  <BaseTemplate app-name="Whatsapp" page-title="">
    <template #content>
      <div class="container mx-auto p-4">
        <!-- Header with actions -->
        <div class="flex justify-between items-center mb-6">
          <h1 class="text-2xl font-bold" :style="{ color: 'var(--clinic-font-color)' }">Máscaras de Laudos</h1>
          <div class="flex space-x-2">
            <button 
              v-if="canCreateMascara"
              @click="openNewMascaraModal" 
              class="clinic-btn flex items-center px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              Nova Máscara
            </button>
            <div v-if="!canCreateMascara && !canEditMascara && !canDeleteMascara" class="text-sm text-gray-600 dark:text-gray-400 bg-yellow-100 dark:bg-yellow-900/20 px-3 py-2 rounded-md">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Modo apenas visualização
            </div>
          </div>
        </div>

        <!-- Search and filter -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6">
          <div class="flex flex-col md:flex-row md:items-center gap-4">
            <div class="relative flex-grow">
              <input 
                type="text" 
                v-model="searchTerm" 
                placeholder="Buscar máscara..." 
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                :style="{ '--tw-ring-color': 'var(--clinic-icon-color)' }"
              />
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <label class="text-sm text-gray-700 dark:text-gray-300">Especialidade:</label>
              <select 
                v-model="filterEspecialidade" 
                class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                :style="{ '--tw-ring-color': 'var(--clinic-icon-color)' }"
              >
                <option value="all">Todas</option>
                <option v-for="esp in especialidades" :key="esp.id" :value="esp.id">{{ esp.descricao }}</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Máscaras Table -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <div v-if="filteredMascaras.length === 0" class="p-8 text-center text-gray-500 dark:text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto mb-4 text-gray-400 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p class="text-lg">Nenhuma máscara de laudo encontrada</p>
            <p class="mt-2">Clique em "Nova Máscara" para criar sua primeira máscara de laudo</p>
          </div>
          
          <div v-else class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead class="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nome</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Especialidade</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Exame</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Criado por</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Última atualização</th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                <tr v-for="(item, index) in filteredMascaras" :key="index" class="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{{ item.nome }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{{ item.especialidade?.descricao || 'N/A' }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{{ item.exame }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{{ item.criado_por }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{{ formatDate(item.ultima_atualizacao) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button 
                      v-if="canEditMascara"
                      @click="editarMascara(index)" 
                      class="mr-3"
                      :style="{ color: 'var(--clinic-icon-color)' }"
                    >
                      <span class="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Editar
                      </span>
                    </button>
                    <button 
                      v-if="canDeleteMascara"
                      @click="confirmDelete(index)" 
                      class="text-red-600 hover:text-red-900 dark:text-red-500 dark:hover:text-red-400"
                    >
                      <span class="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Excluir
                      </span>
                    </button>
                    <span v-if="!canEditMascara && !canDeleteMascara" class="text-gray-500 dark:text-gray-400">
                      Apenas visualização
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
                      <div class="bg-gray-50 dark:bg-gray-900/50 px-6 py-3 flex items-center justify-between">
              <div class="text-sm text-gray-700 dark:text-gray-300">
                Mostrando <span class="font-medium">{{ filteredMascaras.length }}</span> de <span class="font-medium">{{ mascaras.length }}</span> máscaras
              </div>
            </div>
        </div>
      </div>
    </template>
  </BaseTemplate>

  <!-- Modal de Edição/Criação de Máscara -->
  <div v-if="showMascaraModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity" aria-hidden="true" @click="showMascaraModal = false"></div>

      <!-- Modal panel -->
      <div class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
        <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              <h3 class="text-lg leading-6 font-medium" :style="{ color: 'var(--clinic-font-color)' }" id="modal-title">
                {{ editandoIndex !== null ? 'Editar' : 'Nova' }} Máscara de Laudo
              </h3>
              
              <div class="mt-4 w-full">
                <form @submit.prevent="saveMascara" class="space-y-6">
                  <!-- Informações Básicas -->
                  <div class="p-4 rounded-lg bg-gray-100 dark:bg-gray-800">
                    <h3 class="text-lg font-bold mb-4" :style="{ color: 'var(--clinic-icon-color)' }">Informações Básicas</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <!-- Nome da Máscara -->
                      <div>
                        <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Nome da Máscara</label>
                        <input 
                          v-model="mascara.nome" 
                          type="text" 
                          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          :style="{ '--tw-ring-color': 'var(--clinic-icon-color)' }"
                          required
                        />
                      </div>
                      
                      <!-- Especialidade -->
                      <div>
                        <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Especialidade</label>
                        <select 
                          v-model="mascara.especialidadeId" 
                          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          :style="{ '--tw-ring-color': 'var(--clinic-icon-color)' }"
                          required
                        >
                          <option value="">Selecione uma especialidade</option>
                          <option v-for="esp in especialidades" :key="esp.id" :value="esp.id">{{ esp.descricao }}</option>
                        </select>
                      </div>
                      
                      <!-- Tipo de Exame -->
                      <div>
                        <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Tipo de Exame</label>
                        <input 
                          v-model="mascara.exame" 
                          type="text" 
                          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          :style="{ '--tw-ring-color': 'var(--clinic-icon-color)' }"
                          required
                        />
                      </div>
                      
                      <!-- Compartilhar com outros médicos -->
                      <div>
                        <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Compartilhar com outros médicos</label>
                        <div class="flex items-center">
                          <label class="inline-flex items-center cursor-pointer">
                            <input 
                              type="checkbox" 
                              v-model="mascara.compartilhada" 
                              class="w-4 h-4 text-clinic-primary bg-gray-100 border-gray-300 rounded focus:ring-clinic-primary dark:focus:ring-clinic-primary dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                              :style="{ '--tw-ring-color': 'var(--clinic-icon-color)' }"
                            />
                            <span class="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                              {{ mascara.compartilhada ? 'Compartilhada' : 'Não compartilhada' }}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Editor de Conteúdo -->
                  <div class="p-4 bg-gray-50 dark:bg-gray-900/20 rounded-lg">
                    <div class="flex justify-between items-center mb-4">
                      <h3 class="text-lg font-medium text-gray-800 dark:text-gray-300">Conteúdo da Máscara</h3>
                      <div class="flex space-x-2">
                        <button 
                          type="button"
                          @click="insertVariable('paciente')"
                          class="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 text-sm"
                        >
                          {Paciente}
                        </button>
                        <button 
                          type="button"
                          @click="insertVariable('idade')"
                          class="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 text-sm"
                        >
                          {Idade}
                        </button>
                        <button 
                          type="button"
                          @click="insertVariable('data')"
                          class="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 text-sm"
                        >
                          {Data}
                        </button>
                        <button 
                          type="button"
                          @click="insertVariable('medico')"
                          class="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 text-sm"
                        >
                          {Médico}
                        </button>
                      </div>
                    </div>
                    
                    <!-- Editor Quill -->
                    <div class="border border-gray-300 rounded-md bg-white">
                      <QuillEditor
                        ref="quillEditor"
                        v-model:content="mascara.conteudo"
                        contentType="html"
                        :options="quillOptions"
                        theme="snow"
                        style="height: 300px;"
                        class="bg-white"
                      />
                    </div>
                    
                    <!-- Preview -->
                    <div class="mt-4">
                      <h4 class="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Preview:</h4>
                      <div class="p-4 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-h-[100px] prose dark:prose-invert max-w-none" v-html="previewContent"></div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 dark:bg-gray-900/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button 
            type="button" 
            class="clinic-btn w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
            @click="saveMascara"
          >
            {{ editandoIndex !== null ? 'Atualizar' : 'Salvar' }}
          </button>
                      <button 
              type="button" 
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              @click="cancelEdit"
            >
              Cancelar
            </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal de Confirmação de Exclusão -->
  <div v-if="showDeleteModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity" aria-hidden="true"></div>

      <!-- Modal panel -->
      <div class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
        <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
              <svg class="h-6 w-6 text-red-600 dark:text-red-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                Confirmar exclusão
              </h3>
              <div class="mt-2">
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  Tem certeza que deseja excluir esta máscara de laudo? Esta ação não pode ser desfeita.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 dark:bg-gray-900/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button 
            type="button" 
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
            @click="confirmDeleteAction"
          >
            Excluir
          </button>
                      <button 
              type="button" 
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              @click="showDeleteModal = false"
            >
              Cancelar
            </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import BaseTemplate from '../templates/BaseTemplate.vue';
import { ref, reactive, computed, onMounted } from 'vue';
import apiClient from '@/services/authService'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import DOMPurify from 'dompurify';
import { useClinicStore } from '@/stores/settings/clinicStore';
import { useUserStore } from '@/stores/userStore';

// Inicializa o clinicStore (usado para aplicar estilos CSS automaticamente)
useClinicStore();

// Verificação de permissões
const userStore = useUserStore();

// Verifica se o usuário tem as permissões necessárias
const canCreateMascara = computed(() => userStore.permissions.includes('MaskLaudos.create_mascara_laudo'));
const canEditMascara = computed(() => userStore.permissions.includes('MaskLaudos.edit_mascara_laudo'));
const canDeleteMascara = computed(() => userStore.permissions.includes('MaskLaudos.delete_mascara_laudo'));
const canListMascaras = computed(() => userStore.permissions.includes('MaskLaudos.list_mascaras_laudo'));

// Verificação de permissões será feita no onMounted

// --- ESTADO DO COMPONENTE (Refs e Reactives) ---

const mascaras = ref([]);
const especialidades = ref([]); // Adicionado para armazenar as especialidades
const searchTerm = ref('');
const filterEspecialidade = ref('all');

const showMascaraModal = ref(false);
const showDeleteModal = ref(false);
const deleteIndex = ref(null);
const editandoIndex = ref(null);
const quillEditor = ref(null); // Ref para o editor Quill

// Objeto reativo para o formulário do modal
const mascara = reactive({
  id: null,
  nome: '',
  especialidadeId: '',
  exame: '',
  compartilhada: true,
  conteudo: '',
  criadoPor: 'Dr. Usuário Atual', // Isso pode vir do estado global/store
  ultimaAtualizacao: new Date()
});

// Configurações do Quill Editor
const quillOptions = {
  modules: {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'header': [1, 2, 3, false] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link'],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ]
  },
  placeholder: 'Digite o conteúdo da máscara de laudo...',
  theme: 'snow'
};


// --- PROPRIEDADES COMPUTADAS ---

// **CORRIGIDO**: Cria a lista de máscaras filtradas
const filteredMascaras = computed(() => {
  if (!mascaras.value) return [];
  
  return mascaras.value.filter(item => {
    const searchMatch = searchTerm.value.toLowerCase() === '' ||
      item.nome.toLowerCase().includes(searchTerm.value.toLowerCase()) ||
      item.exame.toLowerCase().includes(searchTerm.value.toLowerCase());

    const especialidadeMatch = filterEspecialidade.value === 'all' ||
      item.especialidade?.id == filterEspecialidade.value; // Ajustado para objeto aninhado

    return searchMatch && especialidadeMatch;
  });
});

// **ADICIONADO**: Gera o preview do conteúdo do laudo de forma segura
const previewContent = computed(() => {
  // Como o Quill já gera HTML, só precisamos sanitizar para segurança
  return DOMPurify.sanitize(mascara.conteudo || '');
});


const fetchEspecialidades = async () => {
    try {
        // ATENÇÃO: Substitua pelo endpoint correto da sua API de especialidades
        const response = await apiClient.get('/api/v1/services/list/', );
        especialidades.value = response.data.results; // Ajuste conforme a estrutura da sua resposta
    } catch (error) {
        console.error("Erro ao carregar as especialidades:", error);
    }
};

// Busca as máscaras (sem alterações, já estava ok)
const fetchMascaras = async () => {
  try {
    const response = await apiClient.get('/api/v1/masks/list/', {
      params: { page: 1, page_size: 100 }, // Aumentei o page_size para pegar mais itens, ajuste se necessário
      
    });
    mascaras.value = response.data.results;
  } catch (error) {
    console.error("Erro ao carregar as máscaras:", error);
  }
};

// **ADICIONADO**: Formata a data para exibição na tabela
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const options = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' };
  return new Date(dateString).toLocaleDateString('pt-BR', options);
};


// **ADICIONADO**: Abre o modal para criar uma nova máscara
const openNewMascaraModal = () => {
  if (!canCreateMascara.value) {
    alert('Você não tem permissão para criar máscaras de laudo.');
    return;
  }
  resetForm();
  showMascaraModal.value = true;
};

// Edita uma máscara (ajustado para lidar com especialidade aninhada)
const editarMascara = (index) => {
  if (!canEditMascara.value) {
    alert('Você não tem permissão para editar máscaras de laudo.');
    return;
  }
  const mascaraParaEditar = filteredMascaras.value[index];
  Object.assign(mascara, mascaraParaEditar);
  // O backend espera `especialidadeId`, mas a API retorna um objeto. Ajustamos aqui.
  mascara.especialidadeId = mascaraParaEditar.especialidade.id;
  editandoIndex.value = index;
  showMascaraModal.value = true;
};

// Salva a máscara (ajustado para enviar o ID correto no update)
const saveMascara = async () => {
  try {
    // Verifica permissões antes de salvar
    if (editandoIndex.value !== null && !canEditMascara.value) {
      alert('Você não tem permissão para editar máscaras de laudo.');
      return;
    }
    
    if (editandoIndex.value === null && !canCreateMascara.value) {
      alert('Você não tem permissão para criar máscaras de laudo.');
      return;
    }

    // Payload limpo, enviando apenas o que o formulário preenche
    const payload = {
      nome: mascara.nome,
      exame: mascara.exame,
      compartilhada: mascara.compartilhada,
      conteudo: mascara.conteudo,
      especialidade: mascara.especialidadeId, 
    };

    if (editandoIndex.value !== null) {
      await apiClient.put(`/api/v1/masks/update/${mascara.id}/`, payload);
    } else {
      await apiClient.post('/api/v1/masks/add/', payload);
    }
    
    // Após salvar, busca a lista atualizada e fecha o modal
    fetchMascaras();
    showMascaraModal.value = false;
    resetForm();
  } catch (error) {
    // Log detalhado do erro de validação, se houver
    console.error("Erro ao salvar a máscara:", error.response?.data || error);
  }
};
// **ADICIONADO**: Cancela a edição e fecha o modal
const cancelEdit = () => {
  showMascaraModal.value = false;
  resetForm();
};

// Reseta o formulário
const resetForm = () => {
  mascara.id = null;
  mascara.nome = '';
  mascara.especialidadeId = '';
  mascara.exame = '';
  mascara.compartilhada = true;
  mascara.conteudo = '';
  editandoIndex.value = null;
};

// Lógica de exclusão (ajustada para pegar o ID correto)
const confirmDelete = (index) => {
  if (!canDeleteMascara.value) {
    alert('Você não tem permissão para excluir máscaras de laudo.');
    return;
  }
  deleteIndex.value = filteredMascaras.value[index].id;
  showDeleteModal.value = true;
};

const confirmDeleteAction = async () => {
  if (!canDeleteMascara.value) {
    alert('Você não tem permissão para excluir máscaras de laudo.');
    return;
  }
  
  try {
    await apiClient.delete(`/api/v1/masks/delete/${deleteIndex.value}/`,);
    fetchMascaras(); // Recarrega a lista da API
    deleteIndex.value = null;
    showDeleteModal.value = false;
  } catch (error) {
    console.error("Erro ao excluir a máscara:", error);
  }
};

// **ADICIONADO**: Funções para o editor de texto
const insertVariable = (variable) => {
  if (!quillEditor.value) return;
  
  const quill = quillEditor.value.getQuill();
  const range = quill.getSelection(true);
  const text = `{${variable}}`;
  
  // Insere o texto na posição do cursor
  quill.insertText(range.index, text);
  
  // Posiciona o cursor após a variável inserida
  quill.setSelection(range.index + text.length);
  quill.focus();
};



// --- HOOKS DO CICLO DE VIDA ---

// **MELHORIA**: Usa onMounted para buscar os dados quando o componente é montado
onMounted(() => {
  // Verifica se o usuário tem pelo menos a permissão básica de listar
  if (!canListMascaras.value) {
    console.error('Usuário não tem permissão para acessar máscaras de laudo');
    // Redirecionar para home ou mostrar mensagem de erro
    window.location.href = '/home?unauthorized=true';
    return;
  }
  
  fetchMascaras();
  fetchEspecialidades(); // Não se esqueça de buscar as especialidades
});
</script>

<style>
@import 'tailwindcss/base';
@import 'tailwindcss/components';
@import 'tailwindcss/utilities';

.clinic-btn {
  background-color: var(--clinic-btn-bg);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  border: none;
  cursor: pointer;
}

.clinic-btn:hover:not(:disabled) {
  background-color: var(--clinic-btn-bg-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.clinic-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.input-font-color {
  color: var(--clinic-font-color);
}

/* Estilos adicionais específicos para este componente */
.form-checkbox, .form-radio {
  @apply text-emerald-600 focus:ring-emerald-500;
}

/* Estilos para o toggle switch personalizado */
.toggle-switch {
  transition: all 0.3s ease;
  border: 1px solid #d1d5db;
}

.toggle-switch:checked {
  background-color: var(--clinic-btn-bg) !important;
  border-color: var(--clinic-btn-bg) !important;
}

.toggle-switch:focus {
  box-shadow: 0 0 0 3px rgba(0, 169, 157, 0.1);
  border-color: var(--clinic-btn-bg);
}

/* Melhorar aparência do toggle no modo escuro */
.dark .toggle-switch {
  background-color: #4b5563;
  border-color: #6b7280;
}

.dark .toggle-switch:checked {
  background-color: var(--clinic-btn-bg) !important;
  border-color: var(--clinic-btn-bg) !important;
}

/* Animação suave para o toggle */
.peer:checked ~ div {
  background-color: var(--clinic-btn-bg) !important;
  transition: background-color 0.3s ease;
}

.peer:not(:checked) ~ div {
  background-color: rgb(229 231 235) !important;
  transition: background-color 0.3s ease;
}

.dark .peer:not(:checked) ~ div {
  background-color: #4b5563 !important;
}

/* Classe para cor primária da clínica */
.bg-clinic-primary {
  background-color: var(--clinic-btn-bg) !important;
}

/* Hover para o toggle */
.toggle-switch:hover {
  opacity: 0.9;
  transform: scale(1.02);
}

/* Transição suave para o texto do toggle */
.toggle-switch + span {
  transition: color 0.3s ease;
}

/* Estilo para o indicador interno do toggle */
.toggle-switch::after {
  border-radius: 4px !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Melhorar aparência no modo escuro */
.dark .toggle-switch::after {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

/* Estilos para o tema escuro */
.dark .form-checkbox, .dark .form-radio {
  @apply border-gray-600;
}

/* Transições suaves */
.form-checkbox, .form-radio, button, input, select {
  @apply transition-all duration-200;
}

/* Animações para os modais */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from { transform: translateY(10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.fixed {
  animation: fadeIn 0.3s ease-out;
}

.fixed > div > div {
  animation: slideIn 0.3s ease-out;
}

/* Estilos para o Quill Editor */
.ql-editor {
  min-height: 200px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.6;
}

.ql-toolbar {
  border-top: 1px solid #ccc;
  border-left: 1px solid #ccc;
  border-right: 1px solid #ccc;
}

.ql-container {
  border-bottom: 1px solid #ccc;
  border-left: 1px solid #ccc;
  border-right: 1px solid #ccc;
}

/* Quill Editor sempre com fundo branco e texto preto */
.ql-toolbar {
  border-color: #d1d5db !important;
  background-color: #ffffff !important;
}

.ql-container {
  border-color: #d1d5db !important;
  background-color: #ffffff !important;
}

.ql-editor {
  color: #000000 !important;
  background-color: #ffffff !important;
}

.ql-stroke {
  stroke: #374151 !important;
}

.ql-fill {
  fill: #374151 !important;
}

.ql-picker-label {
  color: #374151 !important;
}

/* Tema escuro para o Quill - mantém sempre branco */
.dark .ql-toolbar {
  border-color: #d1d5db !important;
  background-color: #ffffff !important;
}

.dark .ql-container {
  border-color: #d1d5db !important;
  background-color: #ffffff !important;
}

.dark .ql-editor {
  color: #000000 !important;
  background-color: #ffffff !important;
}

.dark .ql-stroke {
  stroke: #374151 !important;
}

.dark .ql-fill {
  fill: #374151 !important;
}

.dark .ql-picker-label {
  color: #374151 !important;
}

/* Estilos para o preview */
.prose {
  max-width: none;
}

.prose h1 {
  @apply text-xl font-bold mb-4 text-gray-900 dark:text-gray-100;
}

.prose h2 {
  @apply text-lg font-bold mb-3 text-gray-800 dark:text-gray-200;
}

.prose h3 {
  @apply text-base font-bold mb-2 text-gray-800 dark:text-gray-200;
}

.prose p {
  @apply mb-3;
}

.prose ul, .prose ol {
  @apply mb-3 pl-5;
}

.prose ul li {
  @apply list-disc mb-1;
}

.prose ol li {
  @apply list-decimal mb-1;
}

.prose strong {
  @apply font-bold;
}

.prose em {
  @apply italic;
}

.prose a {
  @apply text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300;
}
</style>